import React from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { styled } from '@mui/system';
import { setOpenDrawer } from 'actions/app';
import { isAuthenticated } from 'helpers/authToken';
import { getLocalizedPath } from 'helpers/getLocale';
import { roleAwareTopMenuItem } from 'helpers/menu';
import { useHasRole } from 'hooks/useHasRole';
import { MENU_ITEMS } from 'variables/menu';
import config from 'config';
import { User } from 'lucide-react';
// IconButton + useTheme imported from @mui/material used to land in Menu's
// chunk → Header's chunk → entry chunk (home critical path). Switched to a
// styled button + useTheme from @mui/system. See InlineIconButton below.
import { useTheme } from '@mui/system';
const RenderResources = React.lazy(() => import('components/RenderResources'));
const Drawer = React.lazy(() => import('components/Drawer'));

// Inlined IconButton — Menu is statically imported by Header (on home
// critical path), so any @mui/material import here forces the heavy chunk
// onto the LCP race. This styled button matches MUI IconButton's reset
// (no padding/border/background, circular hit target) and accepts an
// `aria-label` for screen readers.
const InlineIconButton = styled('button')(() => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 8,
  margin: 0,
  border: 'none',
  borderRadius: '50%',
  background: 'transparent',
  cursor: 'pointer',
  appearance: 'none',
  WebkitTapHighlightColor: 'transparent',
  color: 'inherit',
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
  '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: 2 }
}));

const MenuList = styled('ul')(({ footer, isAuth, mobile }) => ({
  display: footer === 'true' ? 'block' : 'flex',
  alignItems: footer === 'true' || mobile === 'true' ? undefined : 'center',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  marginRight: isAuth === 'true' ? 32 : 0,
  ...(mobile === 'true'
    ? {
        marginRight: 0,
        padding: 20,
        flexDirection: 'column'
      }
    : {})
}));

const MenuListItem = styled('li')(({ theme, footer, mobile }) => ({
  margin: '0 20px',
  '& a': {
    textDecoration: 'none',
    fontSize: 14,
    lineHeight: '14px',
    fontWeight: 500,
    fontFamily: 'Rubik-Regular',
    ...(mobile === 'true'
      ? {
          color: footer === 'true' ? theme.palette.white.main : theme.palette.textPrimary.main
        }
      : {})
  },
  ...(mobile === 'true'
    ? {
        margin: 0,
        marginBottom: 20
      }
    : {})
}));

const LinksStyled = styled(Link)(({ theme, active, mobile, transparent }) => ({
  transition: 'color 0.2s',
  color: transparent 
    ? (active === 'true' ? theme.palette.primary.main : theme.palette.textPrimary.main)
    : (active === 'true' ? theme.palette.primary.main : theme.palette.white.main),
  '&:hover': {
    color: theme.palette.primary.main
  },
  ...(mobile === 'true'
    ? {
        color: mobile === 'true' ? theme.palette.textPrimary.main : theme.palette.white.main
      }
    : {})
}));

const FooterMenu = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column'
  },
  '& ul': {
    margin: 0,
    width: '100%',
    [theme.breakpoints.down('md')]: {
      padding: 0
    }
  },
  '& li': {
    margin: 0,
    marginBottom: 30,
    '& a': {
      fontSize: 14
    }
  }
}));

const Menu = ({ isFooter, transparent, userIconOnClick }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useSelector((state) => state.app.isMobile);
  const openDrawer = useSelector((state) => state.app.openDrawer);
  const featuresReady = useSelector((state) => state.features.ready);
  const featureFlags = useSelector((state) => state.features.flags);
  const isConsultant = useHasRole('consultant');
  const isAssessmentAdmin = useHasRole('assessment_admin');
  const isSuperadmin = useHasRole('superadmin');

  const theme = useTheme();

  const tMenu = useTranslate('Menu');
  const tHeader = useTranslate('Header');

  // Re-evaluate on Redux-driven renders so a login or logout can update the
  // menu without requiring a full page reload.
  const isAuth = isAuthenticated();

  const links = React.useMemo(() => {
    const visible = MENU_ITEMS.filter((i) => {
      if (i.hideIfAuth && isAuth) return false;
      // Hide gated items only when /features has resolved AND the flag is
      // explicitly off. Showing-then-hiding for a brief moment beats showing
      // an empty menu while the fetch is in flight (or while the backend is
      // unreachable from a dev machine).
      if (i.feature && featuresReady && featureFlags[i.feature] === false) return false;
      // Brand allowlist (Articles/Education/Vacancies are profitroll-only).
      // Items without `brands` are shown for every brand.
      if (i.brands && config.brand && !i.brands.includes(config.brand)) return false;
      return true;
    });

    if (isFooter) {
      return {
        left: visible.filter(({ bottomLeft }) => bottomLeft),
        right: visible.filter(({ bottomRight }) => bottomRight)
      };
    }

    return visible
      .filter(({ top }) => top)
      .map((item) => roleAwareTopMenuItem(item, isAuth && (isConsultant || isAssessmentAdmin || isSuperadmin)));
  }, [isFooter, isAuth, isConsultant, isAssessmentAdmin, isSuperadmin, featuresReady, featureFlags]);

  const handleCLoseDrawer = React.useCallback(() => {
    dispatch(setOpenDrawer(false));
  }, [dispatch]);

  const RenderList = React.useCallback(
    ({ list = [] }) => {
      return (
        <MenuList footer={`${isFooter}`} isAuth={`${isAuth}`} mobile={`${isMobile}`}>
          {list.map((item) => {
            const label = item.key === 'Cabinet'
              ? tMenu('Cabinet', { defaultValue: tMenu('Profile') })
              : tMenu(item.key);

            return (
              <MenuListItem key={item.key} footer={`${isFooter}`} mobile={`${isMobile}`}>
                <LinksStyled
                  to={getLocalizedPath(`/${item.path}`)}
                  mobile={`${isMobile}`}
                  transparent={transparent}
                  aria-label={label}
                  active={`${(location.pathname || '').includes(item.path)}`}
                  onClick={() => {
                    import('services/analytics').then((Analytics) => {
                      Analytics.default.event('MenuClick', {
                        path: `/${item.path}`
                      });
                    });
                    if (isMobile) {
                      handleCLoseDrawer();
                    }
                  }}>
                  {label}
                </LinksStyled>
              </MenuListItem>
            );
          })}
        </MenuList>
      );
    },
    [isMobile, handleCLoseDrawer, location, isFooter, isAuth, tMenu, transparent]
  );

  if (isMobile && !isFooter) {
    return (
      <Drawer anchor={'right'} open={openDrawer} onClose={handleCLoseDrawer}>
        <RenderResources transparent={transparent} />
        <RenderList list={links} />
        {userIconOnClick && (
          <div style={{ padding: '20px', display: 'flex', justifyContent: 'center', borderTop: `1px solid ${theme.palette.grey[200]}` }}>
            <InlineIconButton
              onClick={() => {
                userIconOnClick();
                handleCLoseDrawer();
              }}
              aria-label={tHeader('UserIcon')}
              style={{
                border: `2px solid ${theme.palette.primary.main}`,
                width: 40,
                height: 40,
                padding: 4,
                color: theme.palette.primary.main
              }}>
              <User
                size={24}
                strokeWidth={2}
                color={theme.palette.primary.main}
                style={{
                  stroke: theme.palette.primary.main,
                  fill: 'none',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  color: theme.palette.primary.main
                }}
              />
            </InlineIconButton>
          </div>
        )}
      </Drawer>
    );
  }

  if (isFooter) {
    return (
      <div>
        <FooterMenu>
          <RenderList list={links?.left} fz={14} />
          <RenderList list={links?.right} fz={14} />
        </FooterMenu>
      </div>
    );
  }

  return (
    <>
      <nav>
        <RenderList list={links} />
      </nav>
      <RenderResources transparent={transparent} />
    </>
  );
};

export default Menu;
